// Per-store membership management. Backed by the user-callable RPCs shipped
// in 20260623050000_products_rewards_module_flags_and_per_store_membership_rpcs:
//
//   core.rpc_org_grant_store_membership(p_org_id, p_user_id, p_store_id, p_role)
//   core.rpc_org_revoke_store_membership(p_membership_id)
//   core.rpc_org_list_user_memberships(p_org_id, p_user_id)
//
// These enforce org-admin role server-side. The companion admin RPCs in
// 20260623040000 are SECURITY DEFINER + service_role-only for ETL paths.

import { supabase } from '../client';
import type { OrgRole } from './integrations';

/** RPCs shipped post-generated.ts regeneration; cast to `any`. */
const sbUntyped = (): any => supabase() as unknown as any; // eslint-disable-line @typescript-eslint/no-explicit-any

export interface UserMembershipGrant {
  membership_id: string;
  user_id: string;
  org_id: string;
  store_id: string | null;
  store_name: string | null;
  role: OrgRole;
  created_at: string;
  archived_at: string | null;
}

export async function listUserMemberships(
  orgId: string,
  userId: string,
): Promise<UserMembershipGrant[]> {
  const { data, error } = await sbUntyped()
    .schema('core')
    .rpc('rpc_org_list_user_memberships', { p_org_id: orgId, p_user_id: userId });
  if (error) throw error;
  return (data ?? []) as unknown as UserMembershipGrant[];
}

export async function grantStoreMembership(input: {
  orgId: string;
  userId: string;
  storeId: string;
  role: 'operator' | 'viewer';
}): Promise<string> {
  const { data, error } = await sbUntyped()
    .schema('core')
    .rpc('rpc_org_grant_store_membership', {
      p_org_id: input.orgId,
      p_user_id: input.userId,
      p_store_id: input.storeId,
      p_role: input.role,
    });
  if (error) throw error;
  return data as unknown as string;
}

export async function revokeStoreMembership(membershipId: string): Promise<boolean> {
  const { data, error } = await sbUntyped()
    .schema('core')
    .rpc('rpc_org_revoke_store_membership', { p_membership_id: membershipId });
  if (error) throw error;
  return Boolean(data);
}

/**
 * Reconcile a member's store access to EXACTLY `storeIds`. Pass an empty
 * array (or omit) to make the member org-wide (access to all stores); pass
 * one or more store ids to scope them to just those locations. Backed by
 * `core.rpc_org_set_member_store_access`.
 *
 * Only `operator`/`viewer` members can be scoped -- the RPC rejects owners
 * and admins (they are always org-wide; lower their role first). Org-admin
 * role is enforced server-side. Seat-neutral: it adds the desired rows
 * before archiving the rest, so an existing member never loses (and is
 * never re-charged for) their seat.
 */
export async function setMemberStoreAccess(input: {
  orgId: string;
  userId: string;
  storeIds: string[];
}): Promise<void> {
  const { error } = await sbUntyped()
    .schema('core')
    .rpc('rpc_org_set_member_store_access', {
      p_org_id: input.orgId,
      p_user_id: input.userId,
      p_store_ids: input.storeIds,
    });
  if (error) throw error;
}
